import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { CartProvider } from "@/context/CartContext";
import { LanguageProvider } from "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import AnnouncementBar from "@/components/AnnouncementBar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFab from "@/components/WhatsAppFab";
import Landing from "@/pages/Landing";
import Shop from "@/pages/Shop";
import Checkout from "@/pages/Checkout";
import Tracking from "@/pages/Tracking";
import ProductDetail from "@/pages/ProductDetail";
import Downloads from "@/pages/Downloads";
import Support from "@/pages/Support";
import Partner from "@/pages/Partner";
import CloudHosting from "@/pages/CloudHosting";
import ServiceStatus from "@/pages/ServiceStatus";
import Apps from "@/pages/Apps";
import KitBuilder from "@/pages/KitBuilder";
import CctvKitBuilder from "@/pages/CctvKitBuilder";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminForgotPassword from "@/pages/admin/AdminForgotPassword";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { trackVisit } from "@/lib/api";

function Shell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (!location.state?.scrollTo && !location.hash) {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    }
    if (!isAdmin) {
      const productMatch = location.pathname.match(/^\/product\/([^/]+)$/);
      trackVisit(location.pathname, productMatch ? productMatch[1] : null);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {!isAdmin && <AnnouncementBar />}
      {!isAdmin && <Navbar />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/tracking" element={<Tracking />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/downloads" element={<Downloads />} />
        <Route path="/support" element={<Support />} />
        <Route path="/partner" element={<Partner />} />
        <Route path="/cloud" element={<CloudHosting />} />
        <Route path="/status" element={<ServiceStatus />} />
        <Route path="/apps" element={<Apps />} />
        <Route path="/kit-builder" element={<KitBuilder />} />
        <Route path="/cctv-kit-builder" element={<CctvKitBuilder />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
        <Route path="/admin/reset-password" element={<AdminResetPassword />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<Landing />} />
      </Routes>
      {!isAdmin && (
        <>
          <Footer />
          <CartDrawer />
          <WhatsAppFab />
        </>
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <CartProvider>
          <BrowserRouter>
            <Shell />
            <Toaster theme="dark" position="bottom-left" />
          </BrowserRouter>
        </CartProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}
